import { api, ApiError, runApiRuntimeLogout } from '@ddone/api-client';
import { FrontendModuleRegistry } from '@ddone/frontend-sdk';
import type { SessionBootstrap, UserSummary, WorkspaceSummary } from '@ddone/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode
} from 'react';
import { purgePrivateBrowserData } from './private-cache';

export const moduleRegistry = new FrontendModuleRegistry();
export const sessionQueryKey = ['session'] as const;

interface AuthContextValue {
  session: SessionBootstrap;
  permissions: ReadonlySet<string>;
  hasPermission(permission?: string): boolean;
  switchWorkspace(workspace: WorkspaceSummary): Promise<void>;
  logout(): Promise<void>;
  refreshSession(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  unauthenticated: ReactNode;
  loading: ReactNode;
  error(error: unknown, retry: () => void): ReactNode;
  setup?(session: SessionBootstrap, refreshSession: () => Promise<void>): ReactNode;
}

export function AuthProvider({
  children,
  unauthenticated,
  loading,
  error,
  setup
}: AuthProviderProps) {
  const queryClient = useQueryClient();
  const previousUser = useRef<string | undefined>(undefined);
  const sessionQuery = useQuery({
    queryKey: sessionQueryKey,
    queryFn: async () => normalizeSession(await api.request<SessionWire>('/session')),
    retry(failureCount, requestError) {
      if (requestError instanceof ApiError && [401, 403].includes(requestError.problem.status))
        return false;
      return failureCount < 2;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true
  });

  const session = sessionQuery.data;
  const permissions = useMemo(() => new Set(session?.permissions ?? []), [session?.permissions]);
  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
  }, [queryClient]);

  useEffect(() => {
    const currentUser = session?.user?.id;
    if (previousUser.current && currentUser && previousUser.current !== currentUser) {
      queryClient.clear();
      void moduleRegistry.purge();
      void purgePrivateBrowserData();
    }
    previousUser.current = currentUser;
  }, [queryClient, session?.user?.id]);

  useEffect(() => {
    if (!session?.authenticated || !session.activeWorkspace) return;
    const eventSource = new EventSource('/api/v1/events', { withCredentials: true });
    const onSecurityChange = () => {
      void moduleRegistry.purge();
      queryClient.clear();
      void refreshSession();
    };
    eventSource.addEventListener('permissions.changed', onSecurityChange);
    eventSource.addEventListener('modules.changed', onSecurityChange);
    eventSource.addEventListener('cache.invalidate', (event) => {
      try {
        const parsed: unknown = JSON.parse((event as MessageEvent<string>).data);
        if (isCacheEvent(parsed)) {
          const queryKey = parsed.payload.queryKey;
          void queryClient.invalidateQueries({ queryKey });
          if (queryKey[0] === 'time-tracking') {
            void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
            void queryClient.invalidateQueries({ queryKey: ['timer'] });
          }
          void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          void queryClient.invalidateQueries({ queryKey: ['suggestions'] });
          void queryClient.invalidateQueries({ queryKey: ['activity'] });
        } else {
          void queryClient.invalidateQueries();
        }
      } catch {
        void queryClient.invalidateQueries();
      }
    });
    return () => eventSource.close();
  }, [queryClient, refreshSession, session?.activeWorkspace, session?.authenticated]);

  useEffect(() => {
    if (!session?.authenticated || !session.activeWorkspace || !session.user) return;
    let cancelled = false;
    const load = async () => {
      await moduleRegistry.purge();
      for (const manifest of session.modules.filter(
        (module) => module.enabled && Boolean(module.frontendEntry)
      )) {
        if (cancelled) return;
        await moduleRegistry.load(manifest, {
          apiBaseUrl: '/api/v1',
          workspaceId: session.activeWorkspace!.id,
          locale: session.user!.locale,
          hasPermission(permission) {
            return permissions.has('*') || permissions.has(permission);
          },
          navigate(href) {
            window.history.pushState({}, '', href);
            window.dispatchEvent(new PopStateEvent('popstate'));
            return Promise.resolve();
          },
          async invalidate() {
            await queryClient.invalidateQueries();
          },
          notify(message, variant = 'info') {
            window.dispatchEvent(
              new CustomEvent('ddone:plugin-notification', { detail: { message, variant } })
            );
          }
        });
      }
    };
    void load().catch((loadError) => {
      window.dispatchEvent(
        new CustomEvent('ddone:plugin-notification', {
          detail: {
            message:
              loadError instanceof Error ? loadError.message : 'Caricamento plugin non riuscito',
            variant: 'error'
          }
        })
      );
    });
    return () => {
      cancelled = true;
    };
  }, [permissions, queryClient, session]);

  const switchWorkspace = useCallback(
    async (workspace: WorkspaceSummary) => {
      await api.request('/session/workspace', {
        method: 'POST',
        body: { workspaceId: workspace.id }
      });
      await moduleRegistry.purge();
      queryClient.clear();
      await purgePrivateBrowserData();
      window.location.assign('/');
    },
    [queryClient]
  );

  const logout = useCallback(async () => {
    let nativeLogout = false;
    let webLogoutUrl: string | undefined;
    try {
      nativeLogout = await runApiRuntimeLogout();
      if (!nativeLogout) {
        const result = await api.request<{ logoutUrl?: string }>('/session/logout', {
          method: 'POST'
        });
        webLogoutUrl = result.logoutUrl;
      }
    } finally {
      await moduleRegistry.purge();
      queryClient.clear();
      await purgePrivateBrowserData();
    }
    if (nativeLogout) window.location.assign('/');
    else window.location.assign(webLogoutUrl ?? '/api/v1/auth/login?loggedOut=true');
  }, [queryClient]);

  const value = useMemo<AuthContextValue | null>(() => {
    if (!session) return null;
    return {
      session,
      permissions,
      hasPermission(permission) {
        return !permission || permissions.has('*') || permissions.has(permission);
      },
      switchWorkspace,
      logout,
      refreshSession
    };
  }, [logout, permissions, refreshSession, session, switchWorkspace]);

  if (sessionQuery.isPending) return loading;
  if (sessionQuery.isError) {
    if (sessionQuery.error instanceof ApiError && sessionQuery.error.problem.status === 401) {
      return unauthenticated;
    }
    return error(sessionQuery.error, () => void sessionQuery.refetch());
  }
  if (!session?.authenticated || !session.user || !value) {
    return unauthenticated;
  }
  if (!session.activeWorkspace) {
    return setup
      ? setup(session, refreshSession)
      : error(
          new Error('Il profilo non dispone di un workspace attivo'),
          () => void refreshSession()
        );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface SessionWire extends Omit<
  SessionBootstrap,
  'modules' | 'user' | 'activeWorkspace' | 'workspaces'
> {
  user?: Omit<UserSummary, 'locale' | 'timezone'> & {
    locale?: string;
    timezone?: string;
    superAdmin?: boolean;
    workspaceAdmin?: boolean;
  };
  activeWorkspace?: WorkspaceSummary & { locale?: string; timezone?: string };
  workspaces: Array<WorkspaceSummary & { locale?: string; timezone?: string }>;
  modules: Array<
    SessionBootstrap['modules'][number] & {
      state?: string;
      permissions?: string[];
    }
  >;
}

function normalizeSession(session: SessionWire): SessionBootstrap {
  const administrative = session.user?.superAdmin || session.user?.workspaceAdmin;
  return {
    ...session,
    user: session.user
      ? {
          ...session.user,
          locale: session.user.locale ?? session.activeWorkspace?.locale ?? 'it',
          timezone: session.user.timezone ?? session.activeWorkspace?.timezone ?? 'Europe/Rome'
        }
      : undefined,
    permissions: administrative ? ['*'] : session.permissions,
    modules: session.modules.map((module) => ({
      ...module,
      enabled: module.enabled ?? module.state === 'ENABLED',
      requiredPermissions: module.requiredPermissions ?? module.permissions ?? []
    }))
  };
}

function isCacheEvent(value: unknown): value is { payload: { queryKey: unknown[] } } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'payload' in value &&
    typeof value.payload === 'object' &&
    value.payload !== null &&
    'queryKey' in value.payload &&
    Array.isArray(value.payload.queryKey)
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth deve essere usato dentro AuthProvider autenticato');
  return value;
}
